import { FC, useContext, useState } from 'react'
import { gql, TypedDocumentNode, useQuery } from '@apollo/client'
import { Button, Input, InputNumber, Select, Spin, Table, Tag } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { translate } from '../../../core'
import { QueryPageableRequest, QueryPageableResponse } from '../../../core/type'
import { usdFormat } from '../../../core/until'
import { Item } from '../type'
import { ItemFindRequest } from '../type/item-query'
import { PurchaseOrderEntryContext, PurchaseOrderEntryCustomItem } from './PurchaseOrderEntryContext'
import styles from './PurchaseOrderEntry.module.scss'

export const ItemQuery: TypedDocumentNode<
  QueryPageableResponse<Item, 'itemsFind'>,
  QueryPageableRequest<ItemFindRequest>
> = gql`
  query GetItems($request: ItemFindRequest!) {
    itemsFind(request: $request) {
      data {
        id
        sku
        description
        costPrice
        vendor {
          contactId
        }
      }
    }
  }
`

const hasCostPrice = (cost?: number | null): cost is number => cost !== null && cost !== undefined

const NoCostPriceTag: FC = () => (
  <Tag bordered={false} color='warning'>{translate('no_cost_price')}</Tag>
)

export const PurchaseOrderEntryItemSearchStepCustomItems: FC = () => {
  const { type, vendorIds, customItems, addCustomItem, updateCustomItem, removeCustomItem } =
    useContext(PurchaseOrderEntryContext)
  const [search, setSearch] = useState('')

  const { data, loading } = useQuery(ItemQuery, {
    variables: {
      request: {
        take: 20,
        skip: 0,
        search: search || undefined,
        types: [type],
        vendorIds,
        archived: false,
      },
    },
  })

  const matches = data?.itemsFind.data ?? []

  const pick = (id: number) => {
    const item = matches.find(match => match.id === id)
    if (!item || !hasCostPrice(item.costPrice))
      return

    addCustomItem({
      id: item.id,
      sku: item.sku,
      description: item.description,
      vendorContactId: item.vendor?.contactId,
      costPrice: item.costPrice,
    })
    setSearch('')
  }

  return (
    <>
      <Select<number>
        className='w-100'
        size='large'
        showSearch
        autoFocus
        value={null}
        searchValue={search}
        onSearch={setSearch}
        onSelect={pick}
        filterOption={false}
        placeholder={translate('stock_sku_search_placeholder')}
        notFoundContent={loading ? <Spin size='small' /> : translate('stock_sku_not_found')}
        options={matches.map(item => ({
          value: item.id,
          disabled: !hasCostPrice(item.costPrice),
          label: (
            <span className={styles.skuOption}>
              <span className={styles.skuOptionText}>
                <strong>{item.sku}</strong> — {item.description}
              </span>
              <span>
                {!hasCostPrice(item.costPrice) && <NoCostPriceTag />}
                {item.vendor?.contactId && <Tag bordered={false}>{item.vendor.contactId}</Tag>}
              </span>
            </span>
          ),
        }))}
      />
      <div className={styles.kbdHint}>{translate('stock_sku_keyboard_hint')}</div>

      {customItems.length
        ? (
          <Table<PurchaseOrderEntryCustomItem>
            className='mt-3'
            size='small'
            scroll={{ x: true }}
            rowKey='id'
            pagination={false}
            dataSource={customItems}
            columns={[
              { dataIndex: 'sku', title: translate('sku'), width: 120 },
              {
                dataIndex: 'description',
                title: translate('description'),
                render: (description: string | undefined, row) => (
                  <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    value={description ?? ''}
                    onChange={e => updateCustomItem(row.id, { description: e.target.value })}
                  />
                ),
              },
              {
                dataIndex: 'quantity',
                title: translate('quantity'),
                width: 100,
                render: (quantity: number, row) => (
                  <InputNumber
                    className='w-100'
                    min={1}
                    precision={0}
                    value={quantity}
                    onChange={value => updateCustomItem(row.id, { quantity: value ?? 0 })}
                  />
                ),
              },
              {
                dataIndex: 'vendorContactId',
                title: translate('vendor'),
                width: 110,
                render: (vendor?: string) => vendor && <Tag bordered={false}>{vendor}</Tag>,
              },
              {
                dataIndex: 'costPrice',
                title: translate('unit_cost'),
                align: 'right',
                width: 120,
                render: (cost?: number | null) => hasCostPrice(cost) ? usdFormat(cost) : <NoCostPriceTag />,
              },
              {
                key: 'total',
                title: translate('total'),
                align: 'right',
                width: 110,
                render: (_, row) => hasCostPrice(row.costPrice) ? usdFormat(row.costPrice * row.quantity) : '—',
              },
              {
                key: 'remove',
                width: 48,
                render: (_, row) => (
                  <Button
                    type='text'
                    icon={<DeleteOutlined />}
                    aria-label={translate('remove')}
                    onClick={() => removeCustomItem(row.id)}
                  />
                ),
              },
            ]}
          />
        )
        : <div className={`${styles.emptyHint} mt-3`}>{translate('stock_sku_empty')}</div>}
    </>
  )
}
